import { ReactNode } from 'react';
import { ImageCard } from './ImageCard';
import type { ImageMetadata } from '../../types/image';

interface ImageGridProps {
  images: ImageMetadata[];
  loading?: boolean;
  loadingMore?: boolean;
  totalCount?: number;
  onDelete?: (mediaId: string) => void;
  onView?: (image: ImageMetadata) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  uploadCard?: ReactNode;
}

/**
 * Grid layout for displaying image cards
 */
export function ImageGrid({
  images,
  loading = false,
  loadingMore = false,
  totalCount = 0,
  onDelete,
  onView,
  onLoadMore,
  hasMore = false,
  uploadCard,
}: ImageGridProps): JSX.Element {
  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '16px',
          padding: '16px',
        }}
      >
        {/* Upload card as first item */}
        {uploadCard && (
          <div
            style={{
              aspectRatio: '1',
              display: 'flex',
              alignItems: 'stretch',
            }}
          >
            {uploadCard}
          </div>
        )}

        {images.map((image) => (
          <ImageCard
            key={image.mediaId}
            image={image}
            onDelete={onDelete}
            onView={onView}
          />
        ))}
      </div>

      {/* Loading indicator */}
      {loading && (
        <div
          style={{
            textAlign: 'center',
            padding: '24px',
            color: 'var(--ink-600)',
          }}
        >
          Loading...
        </div>
      )}

      {/* Pagination section */}
      {!loading && (
        <div
          style={{
            textAlign: 'center',
            padding: '24px',
            borderTop: '1px solid var(--line-soft)',
            marginTop: '16px',
          }}
        >
          {/* Progress bar */}
          {totalCount > 0 && (
            <div style={{ maxWidth: '300px', margin: '0 auto 16px' }}>
              <div
                style={{
                  height: '4px',
                  backgroundColor: 'var(--line-soft)',
                  borderRadius: '2px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${Math.min((images.length / totalCount) * 100, 100)}%`,
                    backgroundColor: 'var(--primary-600)',
                    borderRadius: '2px',
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
              <p style={{ marginTop: '8px', fontSize: '13px', color: 'var(--ink-600)' }}>
                Showing {images.length} of {totalCount} assets
              </p>
            </div>
          )}

          {/* Load more button */}
          {hasMore && onLoadMore && (
            <button
              onClick={onLoadMore}
              disabled={loadingMore}
              style={{
                padding: '12px 32px',
                backgroundColor: loadingMore ? 'var(--primary-200)' : 'var(--primary-600)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: loadingMore ? 'default' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'background-color 0.2s',
              }}
            >
              {loadingMore ? 'Loading...' : 'Load More Assets'}
            </button>
          )}

          {/* All loaded message */}
          {!hasMore && images.length > 0 && (
            <p style={{ fontSize: '13px', color: 'var(--ink-400)', margin: 0 }}>
              All assets loaded
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default ImageGrid;
